import { useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'

interface StarFieldProps {
  count?: number
  radius?: number
}

// Fondo espacial: partículas simulando estrellas con ligero parallax
export default function StarField({ count = 2500, radius = 220 }: StarFieldProps) {
  const groupRef = useRef<THREE.Group>(null)
  const { camera } = useThree()

  const { positions, colors, sizes } = useMemo(() => {
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    const sizes = new Float32Array(count)
    const palette = [
      new THREE.Color('#ffffff'),
      new THREE.Color('#cbd5f5'),
      new THREE.Color('#a5b4fc'),
      new THREE.Color('#fde68a'),
    ]
    for (let i = 0; i < count; i++) {
      // Distribución en un cascarón esférico para envolver la escena
      const r = radius * (0.6 + Math.random() * 0.4)
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta)
      positions[i * 3 + 2] = r * Math.cos(phi)

      const c = palette[Math.floor(Math.random() * palette.length)]
      colors[i * 3] = c.r
      colors[i * 3 + 1] = c.g
      colors[i * 3 + 2] = c.b
      sizes[i] = 0.4 + Math.random() * 1.6
    }
    return { positions, colors, sizes }
  }, [count, radius])

  const texture = useMemo(() => makeStarTexture(), [])

  // Parallax sutil: el campo de estrellas sigue con retardo el movimiento de cámara
  useFrame(() => {
    if (!groupRef.current) return
    groupRef.current.position.set(
      camera.position.x * 0.06,
      camera.position.y * 0.06,
      camera.position.z * 0.06,
    )
    groupRef.current.rotation.y += 0.0002
  })

  return (
    <group ref={groupRef}>
      <points>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[positions, 3]}
          />
          <bufferAttribute attach="attributes-color" args={[colors, 3]} />
          <bufferAttribute attach="attributes-size" args={[sizes, 1]} />
        </bufferGeometry>
        <pointsMaterial
          size={1.4}
          sizeAttenuation
          map={texture}
          vertexColors
          transparent
          opacity={0.9}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </points>
    </group>
  )
}

function makeStarTexture(): THREE.Texture {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const g = ctx.createRadialGradient(
    size / 2,
    size / 2,
    0,
    size / 2,
    size / 2,
    size / 2,
  )
  g.addColorStop(0, 'rgba(255,255,255,1)')
  g.addColorStop(0.2, 'rgba(255,255,255,0.9)')
  g.addColorStop(0.5, 'rgba(255,255,255,0.25)')
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  const tex = new THREE.CanvasTexture(canvas)
  tex.needsUpdate = true
  return tex
}
